/**
 * The referral link: building one to share, and reading one on arrival.
 *
 * ── Why the inbound half matters as much as the outbound half ──
 * A link is only a link if the far end does something with it. Rendering
 * `?ref=94RJNZT5` for people to share while the app ignores that parameter would
 * produce a feature that looks complete and attributes nobody — the invited user lands,
 * sees an empty referral prompt, and types nothing.
 */

/** The query parameter carried by a referral link. */
export const REF_PARAM = 'ref'

/**
 * Where the captured code is held between landing and signing in.
 *
 * `sessionStorage`, not a React state or a module variable: the user arrives on `/?ref=…`,
 * connects a wallet, signs a message, and only then is asked for a code. Any of those
 * steps can involve a redirect back from a wallet app, which remounts the whole tree and
 * would lose anything held in memory. It is session-scoped rather than persistent so a
 * stale code cannot attach itself to a different person on a shared machine.
 */
const STORAGE_KEY = 'ndx.inbound.ref'

/** The alphabet the server generates codes from — no I, L, O, U, 0 or 1. */
const CODE_PATTERN = /^[23456789ABCDEFGHJKMNPQRSTVWXYZ]{4,8}$/

/**
 * Build the link a user shares.
 *
 * Anchored to the CURRENT origin rather than a configured production URL, so a link
 * copied on staging points at staging. A hardcoded domain here would send testers to
 * production with a code that may not exist there.
 */
export function buildReferralLink(code: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin
  return `${origin}/?${REF_PARAM}=${encodeURIComponent(code)}`
}

/**
 * Capture `?ref=` from the URL, once, at app start.
 *
 * The parameter is REMOVED from the address bar afterwards. Two reasons: a user who
 * bookmarks or re-shares the page should not silently pass their inviter's code on to
 * someone else, and the code sitting in the URL would be re-read on every navigation
 * long after it had been used.
 */
export function captureInboundRefCode(): void {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const raw = params.get(REF_PARAM)
  if (!raw) return

  const code = raw.trim().toUpperCase()

  /*
   * Validated before storing. The value came from a URL anyone can edit, and an
   * unchecked one would be handed straight to the bind endpoint — which would reject
   * it, but only after showing the user a prefilled field full of somebody's junk.
   */
  if (CODE_PATTERN.test(code)) {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, code)
    } catch {
      // Private browsing can refuse storage. The prompt still works, just unprefilled.
    }
  }

  params.delete(REF_PARAM)
  const query = params.toString()
  window.history.replaceState(
    {},
    '',
    window.location.pathname + (query ? `?${query}` : '') + window.location.hash,
  )
}

/** The captured code, if this visit came from a referral link. */
export function readInboundRefCode(): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

/** Clear it once attribution is settled, so it cannot be reused for another account. */
export function clearInboundRefCode(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do — a stale key is harmless once the user is bound.
  }
}
